import {
  FORM_FIELD_ARIA_DESCRIBEDBY,
  FORM_FIELD_ARIA_INVALID,
  FORM_FIELD_ARIA_REQUIRED,
  FORM_FIELD_ID,
  FORM_FIELD_WRAPPED,
} from "@/components/Base/FormField/keys";
import { v4 as uuidv4 } from "uuid";
import type { ComputedRef } from "vue";

export interface UseFormFieldAwareOptions {
  id?: string;
}

export interface UseFormFieldAwareReturn {
  isWrapped: boolean;
  effectiveId: ComputedRef<string>;
  ariaBindings: ComputedRef<Record<string, string | true>>;
}

/**
 * Encapsulates the provide/inject pattern for base controls
 * that can be wrapped by BaseFormField.
 *
 * When wrapped, the composable:
 * - Uses the injected id from FormField instead of generating its own
 * - Provides aria attributes (describedby, invalid, required) from the wrapper
 *
 * When standalone, the composable:
 * - Falls back to prop id or a generated UUID
 * - Returns empty aria bindings (the component manages its own)
 */
export function useFormFieldAware(
  options: () => UseFormFieldAwareOptions
): UseFormFieldAwareReturn {
  const isWrapped = inject(FORM_FIELD_WRAPPED, false);
  const injectedId = inject(FORM_FIELD_ID, undefined);
  const injectedAriaDescribedby = inject(
    FORM_FIELD_ARIA_DESCRIBEDBY,
    undefined
  );
  const injectedAriaInvalid = inject(FORM_FIELD_ARIA_INVALID, undefined);
  const injectedAriaRequired = inject(FORM_FIELD_ARIA_REQUIRED, undefined);

  const fallbackId = uuidv4();

  const effectiveId = computed(
    () =>
      (isWrapped ? injectedId?.value : undefined) ?? options().id ?? fallbackId
  );

  const ariaBindings = computed((): Record<string, string | true> => {
    if (!isWrapped) return {};
    const result: Record<string, string | true> = {};
    if (injectedAriaDescribedby?.value) {
      result["aria-describedby"] = injectedAriaDescribedby.value;
    }
    if (injectedAriaInvalid?.value) {
      result["aria-invalid"] = injectedAriaInvalid.value;
    }
    if (injectedAriaRequired?.value) {
      result["aria-required"] = injectedAriaRequired.value;
    }
    return result;
  });

  return { isWrapped, effectiveId, ariaBindings };
}
